import React from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import "./AreaSelection.css";
import useMaterials from "../hooks/materialsData";

const AreaSelection = ({ selectedArea, setSelectedArea }) => {
  const { materials, loading, error } = useMaterials();

  // 🔒 Handle loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <i className="bi bi-arrow-clockwise"></i>
        </div>
        <p className="loading-text">Laden...</p>
      </div>
    );
  }

  // 🔹 Handle error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-card">
          <h3 className="error-title">Er is een fout opgetreden</h3>
          <p className="error-message">{error}</p>
          <p className="error-details">
            Controleer of:
            <ul>
              <li>De Firebase configuratie correct is ingesteld</li>
              <li>De database collectie &quot;biobased_verkenner&quot; bestaat</li>
              <li>Er een internetverbinding is</li>
            </ul>
          </p>
          <Button
            variant="primary"
            onClick={() => window.location.reload()}
            style={{ marginTop: '1rem' }}
          >
            Opnieuw proberen
          </Button>
        </div>
      </div>
    );
  }

  // 🔹 Extract unique applications
  const uniqueApplications = [
    ...new Set(materials.map((mat) => mat.application).filter(Boolean)),
  ];

  // 🔹 Map application names to local image paths
  const imageMap = {
    "Na_isolatie_dak_buitenzijde": "https://nkbb.org/wp-content/uploads/2025/11/Na-isolatie-dak-buitenzijde_01.jpg",
    "Na_isolatie_hellende_daken": "https://nkbb.org/wp-content/uploads/2025/11/06-Hennep-houtwol-of-vlasdekens.jpg",
    "Gedeeltelijke_vloervervanging": "https://nkbb.org/wp-content/uploads/2025/11/Gedeeltelijke-Vloervervanging.jpg",
    "Bestaande_vloer_isoleren": "https://nkbb.org/wp-content/uploads/2025/11/Bestaande-vloer.jpg",
    "Na_isolatie_kruipruimte": "https://nkbb.org/wp-content/uploads/2025/11/Na-isolatie-kruipruimte.jpg",
    "Voorzetgevels": "https://nkbb.org/wp-content/uploads/2025/11/Voorzetgevels_02.jpg",
    "Binnenwanden": "https://nkbb.org/wp-content/uploads/2025/11/Binnenwanden.jpg",
    "Gevel_na_isolatie_buitenzijde": "https://nkbb.org/wp-content/uploads/2025/11/Gevel-na-isolatie-buitenzijde.jpg",
    "Wandafwerking_natte_ruimtes": "https://nkbb.org/wp-content/uploads/2025/11/Wandafwerking-Natte-Ruimtes.jpg",
    "Keukens": "https://nkbb.org/wp-content/uploads/2025/11/Keukens.jpg",
    "Binnendeuren": "https://nkbb.org/wp-content/uploads/2025/11/Binnendeuren.jpg",
    "Kozijnen_en_deuren_buiten": "https://nkbb.org/wp-content/uploads/2025/11/Kozijnen-en-Deuren.jpg"
  };

  return (
    <motion.div
      className="area-selection"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Row>
        {uniqueApplications.map((application) => {
          const imageSrc = imageMap[application] || "/images/default.webp";

          return (
            <Col key={application} md={4} className="mb-4">
              <Card
                className={`area-card ${selectedArea === application ? "selected" : ""}`}
                onClick={() => setSelectedArea(application)}
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              >
                <Card.Img 
                  variant="top" 
                  src={imageSrc} 
                  alt={application.replace(/_/g, ' ')} 
                  className="area-image" 
                />
                <Card.Body>
                  <Card.Title>
                    {application.replace(/_/g, ' ')}
                  </Card.Title>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
      {selectedArea && (
        <motion.div
          className="back-button-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Button
            className="back-button"
            variant="outline-primary"
            onClick={() => setSelectedArea(null)}
            style={{
              marginBottom: '2rem',
              padding: '1rem 2rem',
              backgroundColor: '#d6e0db',
              color: '#0f6c65',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1.1rem'
            }}
          >
            <span className="back-button-text">
              <i className="bi bi-arrow-left me-2"></i>
              Terug naar selectie
            </span>
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

AreaSelection.propTypes = {
  selectedArea: PropTypes.string,
  setSelectedArea: PropTypes.func.isRequired,
};

AreaSelection.propTypes = {
  selectedArea: PropTypes.string,
  setSelectedArea: PropTypes.func.isRequired,
};

export default AreaSelection;
